import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { Badge } from "@/terabizz"
import type { BadgeT, NoBaseline } from "./api"

/** A Home card: Keel card edge, 10px radius, 16px padding, a title row (title, optional right side). */
export function Card({ title, right, children, className = "", id, footer }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; id?: string; footer?: ReactNode }) {
  return (
    <section id={id} className={`tf-card flex min-w-0 flex-col rounded-[var(--k-r-md)] bg-[var(--k-surface)] ${className}`}>
      {(title || right) && (
        <header className="flex min-h-[48px] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 pt-3.5 pb-2 sm:px-5">
          {title && <h2 className="k-h3 text-[15px]">{title}</h2>}
          {right}
        </header>
      )}
      <div className="min-w-0 flex-1 px-4 pb-4 sm:px-5">{children}</div>
      {footer && <div className="border-t border-[var(--k-line)] px-4 py-3 text-[13px] leading-5 text-[var(--k-ink-muted)] sm:px-5">{footer}</div>}
    </section>
  )
}

/**
 * ↑ 36% / ↓ 1.8 pts, green when the move is good for the business, red when not, grey when flat or
 * unknown. With `none` (the comparison is before the launch, partly before it, or zero): "No
 * baseline", no arrow and no %, with the reason in the tooltip.
 */
export function Delta({ pct, pts, good = "up", className = "", none, hint }: { pct?: number | null; pts?: number | null; good?: "up" | "down"; className?: string; none?: NoBaseline | null; hint?: string }) {
  if (none)
    return (
      <span className={`cursor-help whitespace-nowrap text-[var(--k-ink-muted)] underline decoration-dotted underline-offset-2 ${className}`} title={hint} tabIndex={0} data-testid="no-baseline">
        No baseline
      </span>
    )
  const v = pts ?? pct
  if (v == null) return <span className={`text-[var(--k-ink-muted)] ${className}`}>—</span>
  if (v === 0) return <span className={`text-[var(--k-ink-muted)] ${className}`}>0{pts != null ? " pts" : "%"}</span>
  const up = v > 0
  const ok = good === "up" ? up : !up
  const abs = Math.abs(v)
  const txt = pts != null ? `${abs.toFixed(abs < 10 ? 2 : 1).replace(/\.?0+$/, "")} pts` : `${abs >= 100 ? Math.round(abs).toLocaleString("en-US") : abs.toFixed(abs < 10 ? 1 : 0)}%`
  return (
    <span className={`inline-flex items-center gap-0.5 font-medium ${ok ? "text-[var(--k-positive)]" : "text-[var(--k-alert)]"} ${className}`}>
      <span aria-hidden>{up ? "↑" : "↓"}</span>
      <span className="sr-only">{up ? "up" : "down"}</span>
      <span className="k-num">{txt}</span>
    </span>
  )
}

/** A tiny line of the period so far: current solid. Empty values break nothing. */
export function Spark({ values, className = "", color = "var(--k-series-1)" }: { values: (number | null)[]; className?: string; color?: string }) {
  const pts = values.map((v) => (v == null || !Number.isFinite(v) ? null : v))
  const real = pts.filter((v): v is number => v != null)
  if (real.length < 2) return <div className={className} aria-hidden />
  const min = Math.min(...real, 0)
  const max = Math.max(...real)
  const W = 100
  const H = 28
  const x = (i: number) => (i / Math.max(pts.length - 1, 1)) * W
  const y = (v: number) => H - 2 - ((v - min) / (max - min || 1)) * (H - 4)
  let d = ""
  pts.forEach((v, i) => {
    if (v == null) return
    d += `${d && pts[i - 1] != null ? "L" : "M"}${x(i).toFixed(2)},${y(v).toFixed(2)}`
  })
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={className} aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

export function StatusBadge({ b }: { b: BadgeT }) {
  return (
    <span title={b.hint}>
      <Badge tone={b.tone}>{b.label}</Badge>
    </span>
  )
}

/** Segmented control ($ / % of gross, Source / UTM, MID tabs). */
export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; disabled?: boolean; hint?: string }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex shrink-0 items-center rounded-[var(--k-r-sm)] bg-[var(--k-sunken)] p-0.5 shadow-[inset_0_0_0_1px_var(--k-line)]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          title={o.hint}
          onClick={() => onChange(o.value)}
          className={`h-7 whitespace-nowrap rounded-[5px] px-2.5 text-[13px] transition-colors disabled:cursor-not-allowed disabled:text-[var(--tb-ink-4)] ${value === o.value ? "bg-[var(--k-surface)] font-medium text-[var(--k-ink)] shadow-[0_0_0_1px_var(--k-line),var(--k-shadow-raised)]" : "text-[var(--k-ink-muted)] hover:text-[var(--k-ink)]"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function MoreLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="whitespace-nowrap text-[13px] font-medium text-[var(--k-blue)] hover:underline">
      {children}
    </Link>
  )
}

/** Label / value row of a key-figures list. */
export function Row({ label, value, sub, to, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; to?: string; tone?: "alert" }) {
  const inner = (
    <>
      <span className="min-w-0 truncate text-[14px] text-[var(--k-ink)]">{label}</span>
      <span className={`flex shrink-0 items-baseline gap-2 text-right ${tone === "alert" ? "text-[var(--k-alert)]" : "text-[var(--k-ink)]"}`}>
        <span className="k-data text-[14px]">{value}</span>
        {sub && <span className="text-[13px] text-[var(--k-ink-muted)]">{sub}</span>}
        {to && <span aria-hidden className="text-[var(--k-ink-muted)]">›</span>}
      </span>
    </>
  )
  return to ? (
    <Link to={to} className="-mx-2 flex items-center justify-between gap-3 rounded-[var(--k-r-sm)] px-2 py-2.5 hover:bg-[var(--k-blue-50)]">
      {inner}
    </Link>
  ) : (
    <div className="flex items-center justify-between gap-3 py-2.5">{inner}</div>
  )
}

export function Muted({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <p className={`text-[13px] leading-5 text-[var(--k-ink-muted)] ${className}`}>{children}</p>
}
